import { Popover } from '../Shared/Popover';
import { IconButton, List, ListItem, withStyles, WithStyles, Theme, createStyles } from '@material-ui/core';
import LanguageIcon from '@material-ui/icons/Language';
import React from 'react';
import { withLocale } from '../../hocs/withLocale';
import { getLanguages } from 'react-localize-redux';
import { compose } from 'recompose';
import { GELIOS } from 'constants/whileLabel';
import { OrganizationSettings } from '../../types';
import { updateMomentLocale } from '../../utils/translation';

interface TextMap {
  [key: string]: string;
}

interface Language {
  code: string;
  name: string;
  active: boolean;
}

interface Culture {
  [key: string]: any;
}

interface Owner {
  organizationSettings: OrganizationSettings;
  [key: string]: any;
}

interface AlternateDomain {
  id: string;
  href: string;
  live: boolean;
  custom: boolean;
  biz: boolean;
  parent?: any;
  cultures: string[];
  countryCode: string;
  [key: string]: any;
}

interface WithLocaleProps {
  currentLanguage: string;
  locale: any;
  translate: (key: string) => string;
  changeLang: (code: string) => void;
}

const styles = (theme: Theme) =>
  createStyles({
    iconButton: {
      padding: theme.spacing(1),
      minWidth: theme.spacing(4),
    },
    langText: {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    langLink: {
      color: theme.palette.primary.dark,
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
  });

interface LanguageSwitcherProps extends WithLocaleProps, WithStyles<typeof styles> {
  domain: any;
  additionalLanguages?: Language[];
  owner?: Owner;
}

const textMap: TextMap = {
  uk_UA: 'UA',
  ru_UA: 'RU',
  en_US: 'EN',
};

export const LanguageSwitcher = compose(
  withLocale,
  withStyles(styles)
)(
  ({
    currentLanguage,
    classes,
    locale,
    translate,
    changeLang,
    domain,
    additionalLanguages = [],
    owner,
  }: LanguageSwitcherProps) => {
    const langs = getLanguages(locale);
    const currentDomain = domain.domain;
    const alternateDomains = domain.domainsToAlternate;
    const langText = textMap[currentLanguage];

    const isGelios = currentDomain?.owner?.organizationSettings?.clientCabinet === GELIOS;

    const onLangSwitch = (lang: Language, onClose: () => void) => {
      changeLang(lang.code);
      updateMomentLocale(lang.code);
      window.dispatchEvent(
        new CustomEvent('languageChanged', {
          detail: { language: lang.code },
        })
      );

      onClose();
    };

    const allLangs = [...langs, ...additionalLanguages];

    const getLangName = (lang: Language): string => {
      if (lang.code === 'mock') {
        return lang.name;
      } else {
        return translate(`common.lang_${lang.code}`) || lang.name;
      }
    };

    return (
      <Popover
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        renderTrigger={({ onOpen }) => (
          <IconButton
            color="inherit"
            className={classes.iconButton}
            onClick={({ currentTarget }) => onOpen(currentTarget)}
          >
            {langText ? <span className={classes.langText}>{langText}</span> : <LanguageIcon />}
          </IconButton>
        )}
        renderContent={({ onClose }) => (
          <List>
            {allLangs.map((lang: Language) => (
              <ListItem
                key={lang.code}
                onClick={() => onLangSwitch(lang, onClose)}
                selected={currentLanguage === lang.code}
                button
              >
                {getLangName(lang)}
              </ListItem>
            ))}
            {!isGelios &&
              !currentDomain?.biz &&
              !currentDomain?.custom &&
              alternateDomains &&
              alternateDomains.map((alternateDomain: AlternateDomain) => {
                if (
                  currentDomain?.id !== alternateDomain.id &&
                  !alternateDomain.custom &&
                  !alternateDomain.biz &&
                  alternateDomain.live &&
                  !alternateDomain.parent
                ) {
                  return alternateDomain.cultures.map((culture: string) => {
                    return (
                      <ListItem button key={`${alternateDomain.id}-${culture}`}>
                        <a className={classes.langLink} href={`${alternateDomain.href}${window.location.pathname}`}>
                          {translate(`common.lang_${culture}`)}({alternateDomain.countryCode})
                        </a>
                      </ListItem>
                    );
                  });
                }
                return null;
              })}
          </List>
        )}
      />
    );
  }
);
